// src/components/landingpage/LandingPageNav.jsx
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { useNavigate } from 'react-router-dom';
import ApplyFlowDialogs from '../apply/ApplyFlowDialogs';
import { typeRole } from '../../theme/typeRoles';

// Every nav link (desktop buttons, dropdowns, mobile menu) follows the
// /dashboard/typography "Main navigation links" role.
const NAV = typeRole('nav-main');

function LandingPageNav({ hideApply = false }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [loanProgramsAnchorEl, setLoanProgramsAnchorEl] = useState(null);
  const [alternativeOptionsAnchorEl, setAlternativeOptionsAnchorEl] = useState(null);
  const [mobileLoanProgramsOpen, setMobileLoanProgramsOpen] = useState(false);
  const [mobileAlternativeOptionsOpen, setMobileAlternativeOptionsOpen] = useState(false);

  const [openApplyFlow, setOpenApplyFlow] = useState(false);

  // Nested loan-programs menu structure.
  // Items with `children` render as a sub-popout (Alternative Options).
  const loanProgramItems = [
    { label: 'All Programs', url: '/loan-programs' },
    { label: 'Conventional Financing', url: '/conventional' },
    { label: 'VA Loans', url: '/va' },
    { label: 'FHA Loans', url: '/fha' },
    { label: 'USDA Loans', url: '/usda' },
    { label: 'Refinancing', url: '/refinancing' },
    { label: 'Jumbo Loans', url: '/jumbo' },
    {
      label: 'Alternative Options',
      children: [
        { label: 'First Time Homebuyers', url: '/first-time-homebuyers' },
        { label: 'Low Down Payment', url: '/low-down-payment' },
        { label: 'DSCR', url: '/dscr' },
        { label: 'ARM', url: '/arm' },
      ],
    },
  ];

  // Helper: close all nav popovers (used after navigation).
  const closeAllMenus = () => {
    setAnchorEl(null);
    setResourcesAnchorEl(null);
    setLoanProgramsAnchorEl(null);
    setAlternativeOptionsAnchorEl(null);
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = (url) => {
    setAnchorEl(null);
    if (url) navigate(url);
  };

  // ⭐ NEW: Careers added before Contact Us
  const menuItems = [
    { label: 'Find A Loan Officer', url: '/find-a-loan-officer' },
    { label: 'Submit A Payment', url: '/submit-payment' },
    { label: 'Careers', external: 'https://careers.dreamfindershomes.com/' },
    { label: 'Contact Us', url: '/contact' },
  ];

  const startApplyFlow = () => setOpenApplyFlow(true);

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: '#1c1e2a', px: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>

          {/* Logo */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img
              src="/assets/logo-jet2.png"
              alt="Logo"
              style={{ height: 40, marginRight: 16 }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

              <Button
                sx={{ color: 'white', ...NAV }}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setResourcesAnchorEl(e.currentTarget)}
              >
                Mortgage Resources
              </Button>
              <Menu
                anchorEl={resourcesAnchorEl}
                open={Boolean(resourcesAnchorEl)}
                onClose={() => setResourcesAnchorEl(null)}
              >
                <MenuItem sx={NAV} onClick={() => { setResourcesAnchorEl(null); navigate('/calculators'); }}>Calculators</MenuItem>
                <MenuItem sx={NAV} onClick={() => { setResourcesAnchorEl(null); navigate('/blog'); }}>Blog</MenuItem>
                <MenuItem
                  onClick={(e) => { setLoanProgramsAnchorEl(e.currentTarget); }}
                  sx={{ ...NAV, display: 'flex', justifyContent: 'space-between' }}
                >
                  Loan Programs <ArrowRightIcon fontSize="small" />
                </MenuItem>
                <MenuItem sx={NAV} onClick={() => { setResourcesAnchorEl(null); navigate('/purchase'); }}>Purchase</MenuItem>
                <MenuItem sx={NAV} onClick={() => { setResourcesAnchorEl(null); navigate('/refinance'); }}>Refinance</MenuItem>
              </Menu>

              {/* Loan Programs Sub-Menu (level 2) */}
              <Menu
                anchorEl={loanProgramsAnchorEl}
                open={Boolean(loanProgramsAnchorEl)}
                onClose={() => setLoanProgramsAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {loanProgramItems.map((item) => {
                  if (item.children) {
                    // Render parent of a third-level popout (Alternative Options).
                    return (
                      <MenuItem
                        key={item.label}
                        onClick={(e) => setAlternativeOptionsAnchorEl(e.currentTarget)}
                        sx={{ ...NAV, display: 'flex', justifyContent: 'space-between' }}
                      >
                        {item.label} <ArrowRightIcon fontSize="small" />
                      </MenuItem>
                    );
                  }
                  return (
                    <MenuItem
                      key={item.url}
                      onClick={() => {
                        closeAllMenus();
                        navigate(item.url);
                      }}
                      sx={item.url === '/loan-programs' ? { ...NAV, fontWeight: 700 } : NAV}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </Menu>

              {/* Alternative Options Sub-Sub-Menu (level 3) */}
              <Menu
                anchorEl={alternativeOptionsAnchorEl}
                open={Boolean(alternativeOptionsAnchorEl)}
                onClose={() => setAlternativeOptionsAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {(loanProgramItems.find((i) => i.children)?.children ?? []).map((child) => (
                  <MenuItem
                    key={child.url}
                    sx={NAV}
                    onClick={() => {
                      closeAllMenus();
                      navigate(child.url);
                    }}
                  >
                    {child.label}
                  </MenuItem>
                ))}
              </Menu>

              {/* ⭐ UPDATED menu items (supports external link) */}
              {menuItems.map((item, index) =>
                item.external ? (
                  <Button
                    key={index}
                    sx={{ color: 'white', ...NAV }}
                    onClick={() =>
                      window.open(item.external, '_blank', 'noopener,noreferrer')
                    }
                  >
                    {item.label}
                  </Button>
                ) : (
                  <Button
                    key={index}
                    sx={{ color: 'white', ...NAV }}
                    onClick={() => navigate(item.url)}
                  >
                    {item.label}
                  </Button>
                )
              )}

              {!hideApply && (
  <Button
    variant="contained"
    sx={{
      backgroundColor: '#b89b5e',
      color: 'white',
      borderRadius: 1,
      px: 3,
      '&:hover': { backgroundColor: '#ab9767' },
    }}
    onClick={startApplyFlow}
  >
    Apply Now
  </Button>
)}

            </Box>
          )}

          {/* Mobile Menu */}
          {isMobile && (
            <>
              <IconButton edge="end" color="inherit" onClick={handleMenuOpen}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem sx={NAV} onClick={() => handleMenuClose('/')}>Home</MenuItem>

                {/* Mortgage Resources sub-items (matches desktop) */}
                <MenuItem disabled sx={{ opacity: 0.6, ...typeRole('h5-h6') }}>
                  Mortgage Resources
                </MenuItem>
                <MenuItem onClick={() => handleMenuClose('/calculators')} sx={{ ...NAV, pl: 4 }}>
                  Calculators
                </MenuItem>
                <MenuItem onClick={() => handleMenuClose('/blog')} sx={{ ...NAV, pl: 4 }}>
                  Blog
                </MenuItem>
                <MenuItem
                  onClick={() => setMobileLoanProgramsOpen(!mobileLoanProgramsOpen)}
                  sx={{ ...NAV, pl: 4, display: 'flex', justifyContent: 'space-between' }}
                >
                  Loan Programs <ArrowDropDownIcon fontSize="small" sx={{ transform: mobileLoanProgramsOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                </MenuItem>
                {mobileLoanProgramsOpen && loanProgramItems.map((item) => {
                  if (item.children) {
                    return (
                      <React.Fragment key={item.label}>
                        <MenuItem
                          onClick={() => setMobileAlternativeOptionsOpen(!mobileAlternativeOptionsOpen)}
                          sx={{ ...NAV, pl: 6, display: 'flex', justifyContent: 'space-between' }}
                        >
                          {item.label}
                          <ArrowDropDownIcon
                            fontSize="small"
                            sx={{
                              transform: mobileAlternativeOptionsOpen ? 'rotate(180deg)' : 'none',
                              transition: '0.2s',
                            }}
                          />
                        </MenuItem>
                        {mobileAlternativeOptionsOpen && item.children.map((child) => (
                          <MenuItem
                            key={child.url}
                            onClick={() => handleMenuClose(child.url)}
                            sx={{ ...NAV, pl: 8 }}
                          >
                            {child.label}
                          </MenuItem>
                        ))}
                      </React.Fragment>
                    );
                  }
                  return (
                    <MenuItem
                      key={item.url}
                      onClick={() => handleMenuClose(item.url)}
                      sx={{ ...NAV, pl: 6 }}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
                <MenuItem onClick={() => handleMenuClose('/purchase')} sx={{ ...NAV, pl: 4 }}>
                  Purchase
                </MenuItem>
                <MenuItem onClick={() => handleMenuClose('/refinance')} sx={{ ...NAV, pl: 4 }}>
                  Refinance
                </MenuItem>

                {menuItems.map((item, index) => (
                  <MenuItem
                    key={index}
                    sx={NAV}
                    onClick={() => {
                      setAnchorEl(null);
                      if (item.external) {
                        window.open(item.external, '_blank', 'noopener,noreferrer');
                      } else {
                        navigate(item.url);
                      }
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}

                {!hideApply && (
                  <MenuItem
                    sx={NAV}
                    onClick={() => {
                      setAnchorEl(null);
                      startApplyFlow();
                    }}
                  >
                    Apply Now
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Apply Now two-step modal flow (shared with ApplyFlowButton) */}
      <ApplyFlowDialogs open={openApplyFlow} onClose={() => setOpenApplyFlow(false)} />
    </>
  );
}

export default LandingPageNav;
